import { useEffect, useState } from "react";
import axios from "axios";
import { Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public social-proof component for the cert showcase + pricing + homepage.
 *
 * Pulls the live trust-count from either:
 *   • /api/certificates/public/trust-count   (scope="cert", default) — counts
 *     cert engagement only (beta + trial + paid + notify-me + invites)
 *   • /api/certificates/public/app-trust-count (scope="app") — counts EVERY
 *     QuoteSorted user (free + paid + cert + non-cert). Used on the homepage
 *     so the badge is truthful for a whole-app page.
 *
 * Below threshold (default 250 — admin tweakable) the endpoint returns
 * `show: false` and the actual number is never leaked publicly. In that
 * case we render the tagline-only fallback.
 *
 * Above threshold we render the TIER value (250, 500, 1k, 2k, ...) with a
 * short count-up animation. The exact live count is never exposed publicly.
 *
 * Props:
 *   variant: "dark-amber" | "dark-orange" | "light"
 *   scope:   "cert" (default) | "app"
 */
export const TrustCount = ({ variant = "dark-amber", scope = "cert" }) => {
  const [data, setData] = useState(null);
  const [animatedCount, setAnimatedCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const endpoint =
      scope === "app"
        ? `${API}/api/certificates/public/app-trust-count`
        : `${API}/api/certificates/public/trust-count`;
    axios
      .get(endpoint)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        // Fail open — render the tagline fallback if the API is down.
        if (!cancelled) setData({ show: false, display_count: null });
      });
    return () => {
      cancelled = true;
    };
  }, [scope]);

  // Count-up animation when we have a real number to show.
  useEffect(() => {
    if (!data?.show || typeof data.display_count !== "number") return;
    const target = data.display_count;
    const duration = 1100;
    const startedAt = performance.now();
    let frame;
    const tick = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      // Ease-out cubic — quick start, gentle settle.
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedCount(Math.round(eased * target));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [data]);

  // Theme tokens per variant — keeps the component self-contained
  // rather than leaking Tailwind classes via props.
  const themes = {
    "dark-amber": {
      wrap: "inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30",
      icon: "text-amber-500",
      count: "text-amber-300 font-bold",
      label: "text-slate-300 text-sm",
    },
    "dark-orange": {
      wrap: "inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/30",
      icon: "text-orange-500",
      count: "text-orange-300 font-bold",
      label: "text-slate-300 text-sm",
    },
    light: {
      wrap: "inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200",
      icon: "text-amber-600",
      count: "text-amber-700 font-bold",
      label: "text-zinc-600 text-sm",
    },
  };
  const t = themes[variant] || themes["dark-amber"];

  // Still loading — render nothing so we don't flash the wrong state.
  if (data === null) return null;

  // Below threshold → tagline-only fallback.
  if (!data.show || typeof data.display_count !== "number") {
    return (
      <div className={t.wrap} data-testid="trust-count-tagline">
        <Zap className={`w-4 h-4 ${t.icon}`} />
        <span className={t.label}>Built for tradies by a tradey</span>
      </div>
    );
  }

  // Above threshold → live count.
  return (
    <div className={t.wrap} data-testid="trust-count-live">
      <Zap className={`w-4 h-4 ${t.icon}`} />
      <span className={t.label}>Joined by</span>
      <span className={t.count}>{animatedCount.toLocaleString()}+</span>
      <span className={t.label}>UK tradespeople</span>
    </div>
  );
};

export default TrustCount;
